import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { 
  CssBaseline, 
  ThemeProvider, 
  createTheme, 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  IconButton,
  useMediaQuery,
  Button,
  Menu,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ProjectsIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PriceIcon from '@mui/icons-material/AttachMoney';
import LightbulbIcon from '@mui/icons-material/Lightbulb'; // ✅ ADDED - Suggestions icon

// Marketing Pages (PUBLIC) - located in src/pages/
import LandingPage from './pages/LandingPage';
import AboutUs from './pages/AboutUs';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';

// App Components (PROTECTED)
import Dashboard from './components/Dashboard';
import DatabaseMode from './components/DatabaseMode';
import ProjectDetails from './components/ProjectDetails';
import ProjectsList from './components/ProjectsList';
import Settings from './components/Settings';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Pricing from './components/Pricing';
import UserProfile from './components/UserProfile';
import IndustryThemeGenerator from './components/generation/IndustryThemeGenerator';
import Footer from './components/Footer';

// Import Suggestions Page
import Suggestions from './pages/Suggestions'; // ✅ ADDED - Suggestions system

// Import Business Profile Wizard
import ProfileWizard from './components/BusinessProfile/ProfileWizard';
// Import Business Profile Dashboard
import ProfileDashboard from './components/BusinessProfile/ProfileDashboard';

// Import Auth Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
// Import Theme Context
import { ThemeContextProvider, useThemeContext } from './contexts/ThemeContext';

/**
 * Build the MUI theme. Accepts a darkMode boolean so the theme can be
 * recreated whenever the user toggles dark mode in Settings.
 */
function buildTheme(darkMode) {
  return createTheme({
  palette: {
    mode: darkMode ? 'dark' : 'light',
    primary: {
      main: '#6B46C1',
      light: '#8B5CF6',
      dark: '#553C9A',
    },
    secondary: {
      main: darkMode ? '#2D2D3A' : '#F8FAFC',
      contrastText: '#6B46C1',
    },
    background: {
      default: darkMode ? '#121212' : '#FAFAFA',
      paper: darkMode ? '#1E1E2E' : '#FFFFFF',
    },
    accent: {
      main: '#8B5CF6',
      light: '#C4B5FD',
      dark: '#6D28D9',
    }
  },
  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          boxShadow: '0 4px 12px rgba(107, 70, 193, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(107, 70, 193, 0.4)',
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #6B46C1 0%, #553C9A 100%)',
          boxShadow: '0 4px 12px rgba(107, 70, 193, 0.2)',
        }
      }
    }
  }
});
}

// Drawer width
const drawerWidth = 240;

// Styled components
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: drawerWidth,
    }),
  }),
);

const AppBarStyled = styled(AppBar, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

// WordExPress Logo Component
const WordExPressLogo = () => (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: 1,
  }}>
    <img 
      src="/assets/images/WordExPress_logo.png" 
      alt="WordExPress" 
      style={{ 
        height: '40px',
        maxWidth: '200px',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
      }}
    />
  </Box>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Container sx={{ mt: 8, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * Main App Content Component
 */
const AppContent = () => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { darkMode } = useThemeContext();
  const theme = React.useMemo(() => buildTheme(darkMode), [darkMode]);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user, logout } = useAuth();
  
  // Handle drawer open/close
  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };
  
  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };
  
  // Close drawer on mobile after navigation
  const handleNavigation = () => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  // Handle user menu
  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
  };
  
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBarStyled position="fixed" open={drawerOpen && isAuthenticated}>
        <Toolbar>
          {isAuthenticated && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2, ...(drawerOpen && { display: 'none' }) }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* WordExPress Logo */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <WordExPressLogo />
            </Link>
          </Box>

          {/* Auth Buttons */}
          {isAuthenticated ? (
            <>
              <Button 
                color="inherit" 
                component={Link} 
                to="/pricing"
                sx={{ 
                  mr: 1,
                  background: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.2)',
                  }
                }}
              >
                Pricing
              </Button>
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                sx={{ 
                  ml: 1,
                  background: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.2)',
                  }
                }}
              >
                <AccountCircleIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    borderRadius: 2,
                    boxShadow: '0 8px 24px rgba(107, 70, 193, 0.15)',
                  }
                }}
              >
                <MenuItem disabled>
                  <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem component={Link} to="/profile" onClick={handleUserMenuClose}>
                  Profile
                </MenuItem>
                <MenuItem component={Link} to="/settings" onClick={handleUserMenuClose}>
                  Settings
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                color="inherit" 
                component={Link} 
                to="/blog"
                sx={{ 
                  background: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.2)',
                  }
                }}
              >
                Blog
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/pricing"
                sx={{ 
                  background: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.2)',
                  }
                }}
              >
                Pricing
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/login"
                sx={{ 
                  background: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.2)',
                  }
                }}
              >
                Login
              </Button>
              <Button 
                variant="contained" 
                component={Link} 
                to="/register"
                sx={{ 
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  }
                }}
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBarStyled>
      
      {/* Navigation Drawer - Only show when authenticated */}
      {isAuthenticated && (
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(107, 70, 193, 0.12)',
            },
          }}
          variant={isMobile ? 'temporary' : 'persistent'}
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerClose}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeftIcon />
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            <ListItem button component={Link} to="/dashboard" onClick={handleNavigation}>
              <ListItemIcon sx={{ color: 'primary.main' }}>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            
            <ListItem button component={Link} to="/quick-themes" onClick={handleNavigation}>
              <ListItemIcon sx={{ color: 'success.main' }}>
                <BoltIcon />
              </ListItemIcon>
              <ListItemText primary="Quick Themes" />
            </ListItem>
            
            <ListItem button component={Link} to="/ai-custom/step/1" onClick={handleNavigation}>
              <ListItemIcon sx={{ color: 'primary.main' }}>
                <AutoAwesomeIcon />
              </ListItemIcon>
              <ListItemText primary="Custom Themes" />
            </ListItem>
            
            {/* ✅ ADDED - Suggestions navigation item */}
            <ListItem button component={Link} to="/suggestions" onClick={handleNavigation}>
              <ListItemIcon sx={{ color: 'primary.main' }}>
                <LightbulbIcon />
              </ListItemIcon>
              <ListItemText primary="💡 Suggestions" />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem button component={Link} to="/projects" onClick={handleNavigation}>
              <ListItemIcon sx={{ color: 'primary.main' }}>
                <ProjectsIcon />
              </ListItemIcon>
              <ListItemText primary="Projects" />
            </ListItem>
            <ListItem button component={Link} to="/pricing" onClick={handleNavigation}>
              <ListItemIcon sx={{ color: 'primary.main' }}>
                <PriceIcon />
              </ListItemIcon>
              <ListItemText primary="Pricing" />
            </ListItem>
            <ListItem button component={Link} to="/settings" onClick={handleNavigation}>
              <ListItemIcon sx={{ color: 'primary.main' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
          </List>
        </Drawer>
      )}
      
      {/* Main Content */}
      <Main open={drawerOpen && !isMobile && isAuthenticated}>
        <DrawerHeader />
        <Box sx={{ flexGrow: 1, px: 3 }}>
          <Container maxWidth="lg">
            <Routes>
              {/* ===== PUBLIC MARKETING PAGES ===== */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* ===== PUBLIC AUTH PAGES ===== */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/pricing" element={<Pricing />} />

              {/* ===== PROTECTED APP ROUTES ===== */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/quick-themes"
                element={
                  <ProtectedRoute>
                    <DatabaseMode />
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/ai-custom"
                element={<Navigate to="/ai-custom/step/1" replace />}
              />
              <Route
                path="/ai-custom/step/:stepNum"
                element={
                  <ProtectedRoute>
                    <IndustryThemeGenerator />
                  </ProtectedRoute>
                }
              />
              
              {/* ✅ ADDED - Suggestions protected route */}
              <Route
                path="/suggestions"
                element={
                  <ProtectedRoute>
                    <Suggestions />
                  </ProtectedRoute>
                }
              />

              {/* Business Profile Wizard */}
              <Route
                path="/profile/create"
                element={
                  <ProtectedRoute>
                    <ProfileWizard />
                  </ProtectedRoute>
                }
              />

              {/* Business Profile Dashboard */}
              <Route
                path="/profile/dashboard"
                element={
                  <ProtectedRoute>
                    <ProfileDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Business Profile Edit */}
              <Route
                path="/profile/edit/:id"
                element={
                  <ProtectedRoute>
                    <ProfileWizard />
                  </ProtectedRoute>
                }
              />
              
              {/* Legacy redirects */}
              <Route path="/database" element={<Navigate to="/quick-themes" replace />} />
              <Route path="/custom" element={<Navigate to="/ai-custom/step/1" replace />} />
              <Route path="/industry" element={<Navigate to="/ai-custom/step/1" replace />} />
              
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <ProjectsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <ProjectDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                }
              />

              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Container>
        </Box>
        
        {/* Footer - Always visible */}
        <Footer />
      </Main>
    </Box>
    </ThemeProvider>
  );
};

/**
 * Main App Component with Auth Provider
 */
const App = () => {
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeContextProvider>
  );
};

export default App;